import {
  IPointerEvent,
  Mesh,
  MeshBuilder,
  Observer,
  Plane,
  Quaternion,
  Ray,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { toAngle } from '@modules/standard';
import { onTouchTapObservable } from '@modules/standard/src/observable/CommonObservable';
import { zams } from '@zams/logger/zed/generated/loggerMap';
import { World } from 'miniplex';

import { TourDanjiCameraAPI } from '../../camera';
import { LayerMask } from '../../Constants';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { onUnitActivated } from '../../observable';
import { getTourDanjiZamsParams } from '../../TourDanjiModule';
import { UnitResponse } from '../component/response/Unit';
import { Entity } from '../Entity';

import { RaycastSystem } from './RaycastSystem';

interface DirectionObject {
  ray: Ray;
  mesh: Mesh;
  nextPosition?: Vector3 | undefined;
}

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class InteriorTourMovingSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('InteriorTourMovingSystem');
  static readonly RAY_LENGTH = 2;

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------

  private readonly scene;
  private readonly materialManager;
  private readonly cameraAPI;
  private readonly parent: TransformNode;
  private compassMesh: Mesh;

  private directionObjects = new Map<number, DirectionObject>();

  private onTouchTapObserver: Observer<TouchEvent> | undefined = undefined;

  private selectedUnitResponse: UnitResponse | undefined = undefined;
  constructor({
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
  }) {
    this.scene = babylonjs.scene;
    this.cameraAPI = cameraAPI;
    this.materialManager = materialManager;
    this.parent = new TransformNode('InteriorTour', this.scene);
    this.parent.parent = this.cameraAPI.camera;    
    this.compassMesh = MeshBuilder.CreatePlane("compass", {
        size: 0.4, 
        sideOrientation: Mesh.FRONTSIDE
      }, 
      this.scene);

    this.compassMesh.parent = this.parent;
    this.compassMesh.material = this.materialManager.createMaterial(MaterialType.InteriorTourCompass);    
    this.compassMesh.layerMask = LayerMask.UI;
    for (let i = 0; i < 8; i++) {
      const radian = Math.PI / 4 * i;
      const dirMesh = MeshBuilder.CreatePlane(`dir:${radian * toAngle}`, { width: 0.15, height: 0.15 }, this.scene);
      dirMesh.isPickable = false;
      dirMesh.material = this.materialManager.createMaterial(MaterialType.InteriorTourArrow);
      dirMesh.parent = this.parent;
      dirMesh.rotationQuaternion = Quaternion.RotationAxis(Vector3.LeftHandedBackwardReadOnly, radian);
      dirMesh.position = Vector3.UpReadOnly.applyRotationQuaternion(Quaternion.RotationAxis(Vector3.LeftHandedBackwardReadOnly, radian)).scale(0.3);
      dirMesh.layerMask = LayerMask.UI;
      this.directionObjects.set(radian, { ray: new Ray(Vector3.ZeroReadOnly, Vector3.ZeroReadOnly, InteriorTourMovingSystem.RAY_LENGTH), mesh: dirMesh });      
    }

    this.onTouchTapObserver = onTouchTapObservable.add((event) => {
      this.whenTouchTap(event);
    });

    onUnitActivated.add((unit) => {
      this.whenUnitActivated(unit);
    });
  }

  private checkAvailableViewType = () => {
    const cameraType = this.cameraAPI.getCurrentCameraViewType();
    if (cameraType === 'UnitWindowView' || cameraType === 'FakeUnitWindowView') {
      return true;
    } 
    return false;
  }

  private whenBeforeRender = () => {
    this.parent.setEnabled(this.checkAvailableViewType());
    if (!this.parent.isEnabled()) return;

    const engine = this.scene.getEngine();
    const canvas = engine.getRenderingCanvas()!;
    const compassWidthRate = 0.8 + Math.min(canvas.width / 1920, 1.0);
    const compassHeightRate = 2.5 + Math.min(canvas.height / 1080, 1.0);
    
    this.parent.rotation = new Vector3(Math.PI / compassHeightRate, 0, 0);
    this.parent.position = new Vector3(0, -0.5, 1.5);
    this.parent.scaling = Vector3.One().scale(compassWidthRate);    
  }

  private whenViewMatrixChanged = () => {
    if (!this.parent.isEnabled()) return;

    const camera = this.cameraAPI?.camera;
    if (!camera) return;
    this.compassMesh.rotationQuaternion = Quaternion.RotationAxis(Vector3.LeftHandedBackwardReadOnly, (camera.alpha + Math.PI / 2));

    if (this.cameraAPI.getCurrentCameraViewType() === 'UnitWindowView') {  
      this.directionObjects.forEach((obj, radian) => {
        const nextPosition = this.computeNextMovingPosition(obj, radian);
        if (nextPosition) {
          obj.mesh.setEnabled(true);
        } else {
          obj.mesh.setEnabled(false);
        }
      });
    } else {
      this.directionObjects.forEach((obj) => {
        obj.mesh.setEnabled(false);
      });
    }
  }

  private computeNextMovingPosition = (obj: DirectionObject, radian: number) => {
    const camera = this.cameraAPI?.camera;
    if (!camera) return undefined;

    // 카메라 전방을 기준으로 ray를 쏠 방향을 조정
    const forward = Vector3.TransformNormal(Vector3.LeftHandedForwardReadOnly, camera._worldMatrix);
    const forwardXZ = new Vector3(forward.x, 0, forward.z).normalize();
    const dir = forwardXZ.applyRotationQuaternion(Quaternion.RotationAxis(Vector3.UpReadOnly, radian));
    obj.ray.origin = camera.position;
    obj.ray.direction = dir;

    const pickingInfo = this.scene.pickWithRay(obj.ray, (mesh) => {
      if (mesh.layerMask === LayerMask.Default) return false;
      return (mesh.layerMask & LayerMask.UnitCollider) === LayerMask.UnitCollider;
    }, true);

    const length = pickingInfo?.pickedPoint ? Vector3.Distance(camera.position, pickingInfo.pickedPoint) - 0.1 : InteriorTourMovingSystem.RAY_LENGTH;

    return this.checkFloorHitPosition(camera.position, dir, length, 1.2, obj.ray);
  }

  private checkFloorHitPosition = (origin: Vector3, direction: Vector3, length: number, lengthMin: number, ray: Ray): Vector3 | undefined => {
    if (!direction || length < lengthMin) {
      return undefined;
    }

    const checkPoint = origin.add(direction.scale(length));
    ray.origin = checkPoint;
    ray.direction = Vector3.DownReadOnly;  
    const floorHit = this.scene.pickWithRay(ray, (mesh) => {
      return (mesh.layerMask & LayerMask.UnitCollider) === LayerMask.UnitCollider;
    }, true);

    if (floorHit?.pickedPoint) { 
      if (Vector3.Distance(origin, floorHit.pickedPoint) < lengthMin) return undefined;
      return floorHit.pickedPoint;
    }
    return this.checkFloorHitPosition(origin, direction, length * 0.5, lengthMin, ray);
  }

  private whenTouchTap = (event: TouchEvent | IPointerEvent) => {
    const isTouchEvent = 'TouchEvent' in window && event instanceof TouchEvent;
    const x = isTouchEvent ? event.changedTouches[0]?.clientX : (event as PointerEvent).clientX;
    const y = isTouchEvent ? event.changedTouches[0]?.clientY : (event as PointerEvent).clientY;
    if (x === undefined || y === undefined) return;
    const camera = this.cameraAPI?.camera;
    if (!camera) return;
    const pivot = this.parent.getAbsolutePosition();    
    const pos = this.projectClickToPlane(x, y, Vector3.Up(), pivot.y);
    if (!pos) return;
    const dir0 = pos.subtract(pivot);
    const proj = new Vector3(dir0.x, 0, dir0.z).normalize();
    let csMax = -1;
    let dir = Vector3.Zero();
    const forward = camera.getDirection(Vector3.LeftHandedForwardReadOnly);
    const forwardXZ = new Vector3(forward.x, 0, forward.z).normalize();
    let validDir = false;
    this.directionObjects.forEach((obj, radian) => {
      const direction = forwardXZ.applyRotationQuaternion(Quaternion.RotationAxis(Vector3.UpReadOnly, radian));
      const cs = proj.cosSimilarity(direction);
      if (csMax < cs) {
        csMax = cs;
        dir = direction;
        validDir = obj.mesh.isEnabled();
      }
    });
    if (!validDir) return;
    camera.inertialTargetOffset = dir.normalize().scale(0.3);
    zams.z142({
      ...getTourDanjiZamsParams(),
      zed_dong: this.selectedUnitResponse?.unitMetadata.동 ?? '',
      zed_ho: this.selectedUnitResponse?.unitMetadata.호 ?? '',
    }).send();
  }

  private whenUnitActivated = (unit: UnitResponse | undefined) => {
    this.selectedUnitResponse = unit;
  }

  private projectClickToPlane(clickX: number, clickY: number, planeNormal: Vector3, planeHeight: number): Vector3 | undefined {
    const ray = this.scene.createPickingRay(clickX, clickY, null, null);
    const plane = Plane.FromPositionAndNormal(new Vector3(0, planeHeight, 0), planeNormal);  
    const distance = ray.intersectsPlane(plane);
    if (!distance) return undefined;
    
    return ray.origin.add(ray.direction.scale(distance));
  }

  dispose() {
    this.directionObjects.forEach((obj) => {
      obj.mesh.dispose();
    });
    this.compassMesh.dispose();
    this.parent.dispose();
    this.onTouchTapObserver?.remove();
  }

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'viewMatrixChanged',
      run: this.whenViewMatrixChanged,
    },
    {
      group: 'beforeRender',
      run: this.whenBeforeRender,
    }
  ];
}
